/**
 * Copyright © 2026 OwnersTable Inc. All rights reserved.
 * This source code is proprietary and confidential.
 * Unauthorized copying or distribution is strictly prohibited.
 */

import { useCallback, useEffect, useState } from "react";

export const useInfiniteScrollObserver = ({
  fetchNextPage,
  hasNextPage,
  isFetchingNextPage,
  threshold = 0.1,
}) => {
  const [rootElement, setRootElement] = useState(null);
  const [targetElement, setTargetElement] = useState(null);

  const rootRef = useCallback((node) => {
    setRootElement(node);
  }, []);

  const targetRef = useCallback((node) => {
    setTargetElement(node);
  }, []);

  useEffect(() => {
    if (!rootElement || !targetElement || !hasNextPage) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        // Entries arrive oldest first, so one batch can hold "scrolled out" then "back in":
        // the last entry is the loader's current state.
        if (
          entries[entries.length - 1]?.isIntersecting
          && hasNextPage
          && !isFetchingNextPage
        ) {
          fetchNextPage();
        }
      },
      {
        root: rootElement,
        threshold,
      },
    );

    observer.observe(targetElement);

    return () => observer.disconnect();
  }, [fetchNextPage, hasNextPage, isFetchingNextPage, rootElement, targetElement, threshold]);

  return {
    rootRef,
    targetRef,
  };
};
